import { useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  ArrowLeft,
  CheckCircle2,
  XCircle,
  Package,
  Building2,
  Hash,
  ClipboardList,
  AlertCircle,
  ChevronRight,
} from '@/lib/icons';
import { cn } from '@/lib/utils';

import {
  getDeviceModels,
  getModelVariants,
} from '@/features/device-models/api';
import {
  deviceCatalogKeys,
  invalidateDeviceCatalog,
} from '@/features/device-models/keys';
import {
  BULK_ENROLL_MAX_UNITS,
  bulkEnrollUnits,
} from '@/features/inventory/api';
import { useBranchesQuery } from '@/features/branches/queries';
import type { BulkEnrollResult } from '@/features/inventory/device-unit';
import { ApiError } from '@/lib/api';
import { useToast } from '@/hooks/use-toast';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ActionLoadingOverlay, ButtonSpinner } from '@/components/feedback';

export const Route = createFileRoute('/_layout/inventory/units/enroll')({
  component: EnrollUnitsPage,
});

type EnrollMode = 'single' | 'bulk';

// ── Step Configuration (matches WalkInWizard pattern) ──────────────
interface EnrollStepConfig {
  id: number;
  key: string;
  label: string;
  title: string;
  description: string;
  icon: typeof Package;
}

const STEPS: EnrollStepConfig[] = [
  {
    id: 1,
    key: 'variant',
    label: 'Select Variant',
    title: 'Device Variant',
    description:
      'Choose the device model and variant to enroll into inventory.',
    icon: Package,
  },
  {
    id: 2,
    key: 'branch',
    label: 'Select Branch',
    title: 'Branch Assignment',
    description: 'Optionally assign these units to a branch location.',
    icon: Building2,
  },
  {
    id: 3,
    key: 'imeis',
    label: 'Enter IMEIs',
    title: 'IMEI Entry',
    description:
      'Enter one or more IMEI numbers for the devices being enrolled.',
    icon: Hash,
  },
  {
    id: 4,
    key: 'review',
    label: 'Review & Confirm',
    title: 'Review & Confirm',
    description:
      'Verify all details before confirming enrollment into inventory.',
    icon: ClipboardList,
  },
];

const TOTAL_STEPS = STEPS.length;

// ── Step Icons Map ─────────────────────────────────────────────────
const STEP_ICONS: Record<string, React.ReactNode> = {
  variant: <Package className="h-4 w-4" />,
  branch: <Building2 className="h-4 w-4" />,
  imeis: <Hash className="h-4 w-4" />,
  review: <ClipboardList className="h-4 w-4" />,
};

function EnrollUnitsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [step, setStep] = useState(1);
  const [selectedModelId, setSelectedModelId] = useState<string>('');
  const [selectedVariantId, setSelectedVariantId] = useState<string>('');
  const [selectedBranchId, setSelectedBranchId] = useState<string>('');
  const [enrollMode, setEnrollMode] = useState<EnrollMode>('single');
  const [singleImei, setSingleImei] = useState('');
  const [bulkImeis, setBulkImeis] = useState('');
  const [enrollResult, setEnrollResult] = useState<BulkEnrollResult | null>(
    null
  );

  const { data: modelsData } = useQuery({
    queryKey: deviceCatalogKeys.modelList({ isActive: true }),
    queryFn: () => getDeviceModels({ isActive: true }),
  });

  const { data: variantsData } = useQuery({
    queryKey: deviceCatalogKeys.modelVariants(selectedModelId),
    queryFn: () => getModelVariants(selectedModelId),
    enabled: !!selectedModelId,
  });

  const { data: branchesData } = useBranchesQuery();

  const models = modelsData?.value ?? [];
  const variants = variantsData?.value ?? [];
  const branches = branchesData?.value ?? [];

  const selectedModel = models.find((m) => m.id === selectedModelId);
  const selectedVariant = variants.find((v) => v.id === selectedVariantId);

  // ── Current Step Config ──────────────────────────────────────────
  const currentStepIndex = step - 1;
  const currentStepConfig = STEPS[currentStepIndex];
  const progressPercent =
    step <= TOTAL_STEPS ? (step / TOTAL_STEPS) * 100 : 100;

  // ── Mutation ──────────────────────────────────────────────────────
  // Both Single and Bulk modes route through /bulk-enroll so the lock
  // provider (PayTrigger / Knox / Software) is dispatched at enrolment.
  const enrollMutation = useMutation({
    mutationFn: () => {
      const imeiList =
        enrollMode === 'single'
          ? [singleImei.trim()].filter((imei) => imei.length > 0)
          : bulkImeis
              .split('\n')
              .map((line) => line.trim())
              .filter((line) => line.length > 0);
      return bulkEnrollUnits({
        variantId: selectedVariantId,
        branchId: selectedBranchId || undefined,
        units: imeiList.map((imei) => ({ imei })),
      });
    },
    onSuccess: (result) => {
      invalidateDeviceCatalog(queryClient);

      const value = result.value;
      const successCount = value?.successCount ?? 0;
      const failCount = value?.failureCount ?? 0;

      if (
        enrollMode === 'single' &&
        failCount === 0 &&
        successCount === 1 &&
        value?.successes[0]?.id
      ) {
        toast({
          title: t('common.success'),
          description: t('inventory.enroll.success'),
          variant: 'success' as any,
        });
        navigate({
          to: '/inventory/units/$unitId',
          params: { unitId: value.successes[0].id },
        });
        return;
      }

      setEnrollResult(value ?? null);
      setStep(5);

      if (failCount === 0) {
        toast({
          title: t('common.success'),
          description: t('inventory.enroll.success'),
          variant: 'success' as any,
        });
      } else {
        toast({
          title: 'Partially Enrolled',
          description: `${successCount} succeeded, ${failCount} failed.`,
          variant: 'warning' as any,
        });
      }
    },
    onError: (error: unknown) => {
      const message =
        error instanceof ApiError
          ? (error.errors[0] ?? error.message)
          : t('inventory.enroll.failed');
      toast({
        title: t('common.error'),
        description: message,
        variant: 'destructive',
      });
    },
  });

  const imeiCount =
    enrollMode === 'single'
      ? singleImei.trim()
        ? 1
        : 0
      : bulkImeis
          .split('\n')
          .map((l) => l.trim())
          .filter((l) => l.length > 0).length;

  const overBulkLimit = imeiCount > BULK_ENROLL_MAX_UNITS;

  const canProceedStep1 = selectedVariantId !== '';
  const canProceedStep3 = imeiCount > 0 && !overBulkLimit;

  const handleEnroll = () => {
    enrollMutation.mutate();
  };

  const isEnrolling = enrollMutation.isPending;

  const resetForm = () => {
    setStep(1);
    setEnrollResult(null);
    setSingleImei('');
    setBulkImeis('');
    setSelectedModelId('');
    setSelectedVariantId('');
    setSelectedBranchId('');
  };

  // ── Can navigate to step ─────────────────────────────────────────
  function canGoToStep(targetStep: number): boolean {
    if (targetStep === 1) return true;
    if (targetStep === 2) return canProceedStep1;
    if (targetStep === 3) return canProceedStep1;
    if (targetStep === 4) return canProceedStep1 && canProceedStep3;
    return false;
  }

  // Next step label for mobile
  const nextStepLabel =
    currentStepIndex < TOTAL_STEPS - 1
      ? STEPS[currentStepIndex + 1]?.label
      : null;

  return (
    <div className="p-4 lg:p-6 max-w-4xl mx-auto min-h-[calc(100vh-8rem)]">
      {/* ── Back + Title ───────────────────────────────────────── */}
      <div className="flex items-center gap-3 mb-6">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate({ to: '/inventory/units' })}
          className="shrink-0 rounded-full"
        >
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div>
          <h1 className="text-2xl font-semibold">
            {t('inventory.enroll.title')}
          </h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            Register new device units into inventory
          </p>
        </div>
      </div>

      {/* ── Progress Bar ───────────────────────────────────────── */}
      <div className="w-full h-1.5 bg-muted rounded-full mb-6">
        <div
          className="h-full bg-primary rounded-full transition-all duration-500 ease-out"
          style={{ width: `${progressPercent}%` }}
        />
      </div>

      {step <= TOTAL_STEPS && currentStepConfig && (
        <div className="space-y-6 mb-8">
          {/* ── Step Header ──────────────────────────────────── */}
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
            <div>
              <span className="text-primary/70 font-bold text-xs tracking-widest uppercase mb-2 block">
                Step {String(step).padStart(2, '0')} /{' '}
                {String(TOTAL_STEPS).padStart(2, '0')}
              </span>
              <h2 className="text-3xl font-extrabold text-foreground tracking-tight">
                {currentStepConfig.title}
              </h2>
            </div>
            <div className="hidden md:block">
              <p className="text-muted-foreground text-sm max-w-xs italic opacity-80">
                {currentStepConfig.description}
              </p>
            </div>
          </div>

          {/* ── Stepper Nav ──────────────────────────────────── */}
          <nav className="bg-muted/60 backdrop-blur-md rounded-full p-1.5 flex items-center justify-between shadow-sm">
            {STEPS.map((s, idx) => {
              const isActive = step === s.id;
              const isCompleted = step > s.id;
              const isClickable = isCompleted || canGoToStep(s.id);

              return (
                <button
                  key={s.key}
                  onClick={() => isClickable && setStep(s.id)}
                  disabled={!isClickable && !isActive}
                  className={cn(
                    'flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold tracking-tight transition-all duration-300',
                    isActive &&
                      'bg-primary text-primary-foreground shadow-lg shadow-primary/25',
                    isCompleted &&
                      !isActive &&
                      'text-primary cursor-pointer hover:bg-primary/10',
                    !isActive &&
                      !isCompleted &&
                      'text-muted-foreground/40 cursor-default'
                  )}
                >
                  {isCompleted && !isActive ? (
                    <CheckCircle2 className="h-4 w-4" />
                  ) : (
                    STEP_ICONS[s.key]
                  )}
                  <span className="hidden sm:inline">{s.label}</span>
                </button>
              );
            })}
            {/* Mobile next-step indicator */}
            {nextStepLabel && (
              <div className="sm:hidden pr-4">
                <span className="text-xs font-bold text-primary">
                  Next: {nextStepLabel}
                </span>
              </div>
            )}
          </nav>
        </div>
      )}

      {/* ── Step Content Card ──────────────────────────────────── */}
      <div
        className={cn(
          'rounded-2xl animate-fade-in',
          step <= TOTAL_STEPS &&
            'bg-[hsl(var(--surface-container-lowest))] shadow-xl shadow-foreground/[0.03] p-6 md:p-10'
        )}
      >
        {/* ── Step 1: Select Variant ──────────────────────────── */}
        {step === 1 && (
          <div className="space-y-5 max-w-lg">
            <div>
              <label className="text-sm font-medium mb-1.5 block">
                Device Model
              </label>
              <Select
                value={selectedModelId}
                onValueChange={(v) => {
                  setSelectedModelId(v);
                  setSelectedVariantId('');
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select a device model..." />
                </SelectTrigger>
                <SelectContent>
                  {models.map((m) => (
                    <SelectItem key={m.id} value={m.id}>
                      {m.brand} {m.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {selectedModelId && (
              <div className="animate-fade-in">
                <label className="text-sm font-medium mb-1.5 block">
                  Variant
                </label>
                <Select
                  value={selectedVariantId}
                  onValueChange={setSelectedVariantId}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select a variant..." />
                  </SelectTrigger>
                  <SelectContent>
                    {variants.map((v) => (
                      <SelectItem key={v.id} value={v.id}>
                        {[v.storageCapacity, v.color, v.ram]
                          .filter(Boolean)
                          .join(' / ') || 'Default'}{' '}
                        {v.sku ? `(${v.sku})` : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="flex gap-3 pt-4">
              <Button disabled={!canProceedStep1} onClick={() => setStep(2)}>
                {t('common.actions.next')}
                <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            </div>
          </div>
        )}

        {/* ── Step 2: Select Branch ──────────────────────────── */}
        {step === 2 && (
          <div className="space-y-5 max-w-lg">
            <p className="text-sm text-muted-foreground">
              Optionally assign these units to a branch (can be changed later).
            </p>
            <Select
              value={selectedBranchId}
              onValueChange={setSelectedBranchId}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select a branch (optional)..." />
              </SelectTrigger>
              <SelectContent>
                {branches.map((b) => (
                  <SelectItem key={b.id} value={b.id}>
                    {b.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <div className="flex gap-3 pt-4">
              <Button variant="outline" onClick={() => setStep(1)}>
                <ArrowLeft className="h-4 w-4 mr-1" />
                {t('common.actions.back')}
              </Button>
              <Button onClick={() => setStep(3)}>
                {t('common.actions.next')}
                <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            </div>
          </div>
        )}

        {/* ── Step 3: Enter IMEIs ────────────────────────────── */}
        {step === 3 && (
          <div className="space-y-5 max-w-lg">
            <div className="flex gap-2">
              <Button
                variant={enrollMode === 'single' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setEnrollMode('single')}
              >
                {t('inventory.enroll.singleImei')}
              </Button>
              <Button
                variant={enrollMode === 'bulk' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setEnrollMode('bulk')}
              >
                {t('inventory.enroll.bulkImeis')}
              </Button>
            </div>

            {enrollMode === 'single' ? (
              <Input
                placeholder={t('inventory.enroll.imeiPlaceholder')}
                value={singleImei}
                onChange={(e) => setSingleImei(e.target.value)}
                maxLength={20}
              />
            ) : (
              <Textarea
                placeholder={t('inventory.enroll.bulkPlaceholder')}
                value={bulkImeis}
                onChange={(e) => setBulkImeis(e.target.value)}
                rows={8}
              />
            )}

            {imeiCount > 0 && (
              <div
                className={`flex items-center gap-2 text-sm rounded-xl px-4 py-2.5 ${
                  overBulkLimit
                    ? 'text-destructive bg-destructive/10'
                    : 'text-muted-foreground bg-muted/50'
                }`}
              >
                <Hash className="h-4 w-4" />
                <span>
                  <span className="font-semibold text-foreground">
                    {imeiCount}
                  </span>{' '}
                  IMEI{imeiCount !== 1 ? 's' : ''}{' '}
                  {overBulkLimit
                    ? `— too many for one batch. Enroll at most ${BULK_ENROLL_MAX_UNITS} at a time.`
                    : 'ready to enroll'}
                </span>
              </div>
            )}

            <div className="flex gap-3 pt-4">
              <Button variant="outline" onClick={() => setStep(2)}>
                <ArrowLeft className="h-4 w-4 mr-1" />
                {t('common.actions.back')}
              </Button>
              <Button disabled={!canProceedStep3} onClick={() => setStep(4)}>
                {t('common.actions.next')}
                <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            </div>
          </div>
        )}

        {/* ── Step 4: Review & Confirm ───────────────────────── */}
        {step === 4 && (
          <div className="space-y-5 max-w-lg relative">
            <ActionLoadingOverlay
              loading={isEnrolling}
              loadingTitle={`Enrolling ${imeiCount} Device${imeiCount !== 1 ? 's' : ''}...`}
              loadingSubtitle="Registering device units into inventory"
              className="rounded-2xl"
              steps={[
                { label: 'Validating IMEI numbers', status: 'active' },
                { label: 'Registering in inventory', status: 'pending' },
                { label: 'Assigning to branch', status: 'pending' },
              ]}
            />

            <p className="text-sm text-muted-foreground">
              Please review the details below before confirming enrollment.
            </p>

            <div className="space-y-0 rounded-xl border overflow-hidden">
              <InfoRow
                label="Model"
                value={
                  selectedModel
                    ? `${selectedModel.brand} ${selectedModel.name}`
                    : '-'
                }
              />
              <InfoRow
                label="Variant"
                value={
                  selectedVariant
                    ? [
                        selectedVariant.storageCapacity,
                        selectedVariant.color,
                        selectedVariant.ram,
                      ]
                        .filter(Boolean)
                        .join(' / ') || 'Default'
                    : '-'
                }
              />
              <InfoRow
                label="Branch"
                value={
                  branches.find((b) => b.id === selectedBranchId)?.name ?? (
                    <span className="text-muted-foreground italic">
                      Not assigned
                    </span>
                  )
                }
              />
              <InfoRow
                label="Units to Enroll"
                value={
                  <Badge variant="secondary">
                    {imeiCount} IMEI{imeiCount !== 1 ? 's' : ''}
                  </Badge>
                }
              />
              <InfoRow
                label="Mode"
                value={
                  <Badge variant="outline">
                    {enrollMode === 'single' ? 'Single' : 'Bulk'}
                  </Badge>
                }
              />
            </div>

            <div className="flex gap-3 pt-4">
              <Button
                variant="outline"
                onClick={() => setStep(3)}
                disabled={isEnrolling}
              >
                <ArrowLeft className="h-4 w-4 mr-1" />
                {t('common.actions.back')}
              </Button>
              <Button
                onClick={handleEnroll}
                disabled={isEnrolling}
                className="min-w-[130px]"
              >
                {isEnrolling ? (
                  <>
                    <ButtonSpinner className="mr-2" />
                    {t('inventory.enroll.enrolling')}
                  </>
                ) : (
                  <>
                    <CheckCircle2 className="mr-2 h-4 w-4" />
                    {t('common.actions.confirm')}
                  </>
                )}
              </Button>
            </div>
          </div>
        )}

        {/* ── Step 5: Result (Bulk only) ─────────────────────── */}
        {step === 5 && enrollResult && (
          <div className="space-y-5 max-w-lg mx-auto text-center animate-fade-in">
            {/* Success / Warning Icon */}
            <div className="flex justify-center">
              <div
                className={cn(
                  'h-16 w-16 rounded-2xl flex items-center justify-center',
                  enrollResult.failureCount === 0
                    ? 'bg-emerald-500/10'
                    : 'bg-amber-500/10'
                )}
              >
                {enrollResult.failureCount === 0 ? (
                  <CheckCircle2 className="h-8 w-8 text-emerald-500" />
                ) : (
                  <AlertCircle className="h-8 w-8 text-amber-500" />
                )}
              </div>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-foreground">
                Enrollment Result
              </h2>
              <p className="text-sm text-muted-foreground mt-1">
                {enrollResult.failureCount === 0
                  ? 'All devices enrolled successfully!'
                  : 'Some devices could not be enrolled.'}
              </p>
            </div>

            {/* Summary badges */}
            <div className="flex flex-wrap gap-3 justify-center">
              <div className="flex items-center gap-2 rounded-xl bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 px-4 py-2.5 border border-emerald-500/20">
                <CheckCircle2 className="h-4 w-4 shrink-0" />
                <span className="font-semibold text-sm">
                  {enrollResult.successCount} succeeded
                </span>
              </div>
              {enrollResult.failureCount > 0 && (
                <div className="flex items-center gap-2 rounded-xl bg-destructive/10 text-destructive px-4 py-2.5 border border-destructive/20">
                  <XCircle className="h-4 w-4 shrink-0" />
                  <span className="font-semibold text-sm">
                    {enrollResult.failureCount} failed
                  </span>
                </div>
              )}
            </div>

            {/* Failed IMEIs */}
            {enrollResult.failures.length > 0 && (
              <div className="space-y-2 text-left">
                <p className="text-sm font-medium text-destructive">
                  Failed IMEIs:
                </p>
                <div className="max-h-48 overflow-y-auto space-y-1.5 rounded-xl border border-destructive/20 p-2">
                  {enrollResult.failures.map((f, i) => (
                    <div
                      key={i}
                      className="text-sm p-2.5 rounded-lg bg-destructive/10 text-destructive flex items-start gap-2"
                    >
                      <XCircle className="h-4 w-4 shrink-0 mt-0.5" />
                      <span>
                        <span className="font-mono font-medium">
                          {String(f.imei ?? 'Unknown IMEI')}
                        </span>
                        {' — '}
                        {String(f.error ?? 'Unknown error')}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="flex gap-3 pt-4 justify-center">
              <Button onClick={() => navigate({ to: '/inventory/units' })}>
                {t('inventory.units.title')}
              </Button>
              <Button variant="outline" onClick={resetForm}>
                Enroll More
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function InfoRow({
  label,
  value,
}: Readonly<{ label: string; value: React.ReactNode }>) {
  return (
    <div className="flex justify-between items-center px-4 py-3 border-b border-border/60 last:border-0 bg-background even:bg-muted/30">
      <span className="text-sm text-muted-foreground">{label}</span>
      <span className="text-sm font-medium">{value}</span>
    </div>
  );
}
